import { useState, useEffect } from 'react';
import axios from 'axios';
import { Loader2, Download } from 'lucide-react';
import { UpgradeEmailsEditor } from './UpgradeEmailsEditor';
import { UpsellSettingsPanel, MonthlyReportPanel } from './ReportPanels';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (d) => (d ? new Date(d).toLocaleDateString('en-AU') : '');

const downloadCsv = (name, rows) => {
  const csv = rows.map((r) => r.map((c) => `"${String(c ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
  a.download = name; a.click();
};

const Stat = ({ label, value, testId }) => (
  <div className="bg-white rounded-2xl border border-[#E6DAC5] p-5" data-testid={testId}>
    <p className="text-xs uppercase tracking-[0.15em] text-[#1E1622]/50 mb-1">{label}</p>
    <p className="font-display text-3xl text-[#3B164C]">{value}</p>
  </div>
);

const Table = ({ title, headers, rows, testId, onExport }) => (
  <div className="bg-white rounded-2xl border border-[#E6DAC5] overflow-hidden" data-testid={testId}>
    <div className="flex items-center justify-between p-5 border-b border-[#E6DAC5]">
      <h3 className="font-serif text-lg font-semibold text-[#1E1622]">{title} <span className="text-[#C5A059]">{rows.length}</span></h3>
      {rows.length > 0 && <button onClick={onExport} data-testid={`${testId}-export`} className="btn-secondary text-xs inline-flex items-center gap-1"><Download size={12} /> Export CSV</button>}
    </div>
    {rows.length === 0 ? <p className="p-8 text-center text-sm text-[#1E1622]/50">Nothing yet</p> : (
      <div className="overflow-x-auto"><table className="w-full">
        <thead className="bg-[#F5EFE9]"><tr>{headers.map((h) => <th key={h} className="text-left p-3 text-xs font-semibold text-[#1E1622]">{h}</th>)}</tr></thead>
        <tbody>{rows.map((r, i) => <tr key={i} className="border-t border-[#E6DAC5]">{r.map((c, j) => <td key={j} className="p-3 text-sm text-[#1E1622]/80">{c}</td>)}</tr>)}</tbody>
      </table></div>
    )}
  </div>
);

export const GuideLeadsTab = ({ authHeaders }) => {
  const [d, setD] = useState(null);
  // eslint-disable-next-line
  useEffect(() => { axios.get(`${API}/admin/guide-leads`, authHeaders).then((r) => setD(r.data)).catch(() => setD({ leads: [], upgrade_reminders: [], destination_subscribers: [], destination_alerts_sent: [] })); }, []);
  if (!d) return <Loader2 className="animate-spin text-[#3B164C]" />;

  const leadRows = d.leads.map((l) => [l.email, l.guide_title, fmt(l.created_at), l.status, l.converted ? 'Yes' : 'No']);
  const remRows = d.upgrade_reminders.map((r) => [r.email, r.status, r.due ? `A$${r.due}` : '', fmt(r.sent_at || r.created_at), r.nudge_status ? `${r.nudge_status}${r.nudge_sent_at ? ` ${fmt(r.nudge_sent_at)}` : ''}` : (r.token_expires_at ? `due ${fmt(new Date(new Date(r.token_expires_at) - 3 * 864e5))}` : ''), r.upgraded ? 'Yes' : 'No']);
  const up = d.upsell || { offered: 0, taken: 0, revenue: 0, discount_given: 0, conversion: 0, orders: [] };
  const upRows = up.orders.map((o) => [o.email, fmt(o.date), `A$${o.amount}`, o.credit != null ? `A$${o.credit}` : '', o.payment_method]);
  const destRows = d.destination_subscribers.map((s) => [s.email, s.any_destination ? 'Any new destination' : '', (s.destinations || []).join(', '), fmt(s.created_at)]);
  const sent = d.leads.filter((l) => l.status === 'sent').length;
  const converted = d.leads.filter((l) => l.converted).length;

  return (
    <div className="space-y-6" data-testid="guide-leads-section">
      <h2 className="font-serif text-xl font-semibold text-[#1E1622]">Guide Leads</h2>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Sample sign-ups" value={d.leads.length} testId="leads-stat-total" />
        <Stat label="Follow-ups sent" value={sent} testId="leads-stat-sent" />
        <Stat label="Bought after sample" value={converted} testId="leads-stat-converted" />
        <Stat label="Upgrade reminders sent" value={d.upgrade_reminders.filter((r) => r.status === 'sent').length} testId="leads-stat-reminders" />
      </div>
      <div className="space-y-4" data-testid="upsell-results">
        <h3 className="font-serif text-lg font-semibold text-[#1E1622]">Thank-you page upsell</h3>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Stat label="Offers shown" value={up.offered} testId="upsell-stat-offered" />
          <Stat label="Buyers who upgraded" value={up.taken} testId="upsell-stat-taken" />
          <Stat label="Conversion" value={`${up.conversion}%`} testId="upsell-stat-conversion" />
          <Stat label="Extra revenue" value={`A$${up.revenue}`} testId="upsell-stat-revenue" />
        </div>
        <p className="text-xs text-[#1E1622]/50">Extra revenue is what upgraders paid on top of their first purchase (after A${up.discount_given} of today-only discounts).</p>
        <UpsellSettingsPanel authHeaders={authHeaders} />
        <Table title="Thank-you upgrades" testId="upsell-orders-table" headers={['Email', 'Date', 'Paid', 'Credit used', 'Method']} rows={upRows}
          onExport={() => downloadCsv('thank-you-upsells.csv', [['email', 'date', 'paid', 'credit_used', 'method'], ...upRows])} />
      </div>
      <UpgradeEmailsEditor authHeaders={authHeaders} />
      <MonthlyReportPanel authHeaders={authHeaders} />
      <Table title="Free-sample sign-ups" testId="sample-leads-table" headers={['Email', 'Guide', 'Signed up', 'Follow-up', 'Bought']} rows={leadRows}
        onExport={() => downloadCsv('sample-leads.csv', [['email', 'guide', 'signed_up', 'follow_up', 'bought'], ...leadRows])} />
      <Table title="Upgrade reminders" testId="upgrade-reminders-table" headers={['Email', 'First reminder', 'Offer', 'Date', 'Final nudge', 'Upgraded']} rows={remRows}
        onExport={() => downloadCsv('upgrade-reminders.csv', [['email', 'first_reminder', 'offer', 'date', 'final_nudge', 'upgraded'], ...remRows])} />
      <Table title="New destination subscribers" testId="destination-subscribers-table" headers={['Email', 'Any', 'Destinations', 'Signed up']} rows={destRows}
        onExport={() => downloadCsv('destination-subscribers.csv', [['email', 'any', 'destinations', 'signed_up'], ...destRows])} />
    </div>
  );
};
